import {
  BikeStation,
  ComputedRoute,
  PrintableGeoveloIcon,
  PrintablePlaceIcon,
  Route,
} from '@geovelo-frontends/commons';
import { Document, Font, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { Chart } from 'chart.js';
import { Map } from 'maplibre-gl';
import React from 'react';
import { useTranslation } from 'react-i18next';

import robotoMedium from '../../assets/fonts/Nunito-Medium.ttf';
import robotoRegular from '../../assets/fonts/Nunito-Regular.ttf';

import DataGrid from './components/data-grid';
import Elevation from './components/elevation';
import InstructionsList from './components/instructions-list';

Font.register({
  family: 'Nunito Sans',
  fonts: [
    { src: robotoRegular, fontWeight: 400 },
    { src: robotoMedium, fontWeight: 'medium' },
  ],
});

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Nunito Sans',
    fontSize: 12,
    flexDirection: 'column',
    lineHeight: 1.35,
    padding: '30px 35px 65px',
  },
  header: {
    alignItems: 'center',
    flexDirection: 'row',
    fontSize: 20,
    height: 70,
  },
  headerLogo: {
    marginRight: 16,
    height: 50,
    width: 50,
  },
  wayPointRow: {
    alignItems: 'center',
    flexDirection: 'row',
    height: 25,
  },
  wayPointRowIcon: {
    marginRight: 8,
    height: 20,
    width: 20,
  },
  mapWrapper: {
    alignItems: 'center',
    marginTop: 30,
  },
  map: {
    height: 400,
    width: 400,
  },
  smallMapsWrapper: {
    marginTop: 30,
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  smallMap: {
    height: 200,
    width: 200,
  },
  pageNumber: {
    bottom: 30,
    fontSize: 8,
    left: 0,
    position: 'absolute',
    right: 0,
    textAlign: 'center',
    color: '#333',
  },
});

export interface IDataToPrint {
  arrivalMap: boolean;
  departureMap: boolean;
  elevation: boolean;
  fullMap: boolean;
  instructions: boolean;
}

interface IProps {
  arrivalMap: Map | undefined;
  bikeStationsMap: { [key: number]: BikeStation };
  dataToPrint: IDataToPrint;
  departureMap: Map | undefined;
  elevationChart: Chart | undefined;
  fullMap: Map | undefined;
  route: ComputedRoute | Route;
}

function PrintedRouteLayout({
  dataToPrint,
  bikeStationsMap,
  route,
  elevationChart,
  fullMap,
  departureMap,
  arrivalMap,
}: IProps): React.JSX.Element {
  const { t } = useTranslation();
  const { wayPoints } = route;

  return (
    <Document>
      <Page wrap size="A4" style={styles.page}>
        <View fixed style={styles.header}>
          <PrintableGeoveloIcon height={48} style={styles.headerLogo} width={48} />
          <Text>{t('commons.routing.roadmap.title')}</Text>
        </View>
        <View>
          <View style={styles.wayPointRow}>
            <PrintablePlaceIcon
              color="#2ac682"
              height={20}
              style={styles.wayPointRowIcon}
              width={20}
            />
            <Text>{wayPoints[0].address}</Text>
          </View>
          <View style={styles.wayPointRow}>
            <PrintablePlaceIcon
              color="#dd428d"
              height={20}
              style={styles.wayPointRowIcon}
              width={20}
            />
            <Text>{wayPoints[wayPoints.length - 1].address}</Text>
          </View>
        </View>
        <DataGrid route={route} />
        {dataToPrint.elevation && <Elevation elevationChart={elevationChart} route={route} />}
        {dataToPrint.fullMap && (
          <View style={styles.mapWrapper}>
            <Image src={fullMap?.getCanvas().toDataURL('image/png', 1)} style={styles.map} />
          </View>
        )}
        {(dataToPrint.departureMap || dataToPrint.arrivalMap) && (
          <View style={styles.smallMapsWrapper}>
            {dataToPrint.departureMap && (
              <Image
                src={departureMap?.getCanvas().toDataURL('image/png', 1)}
                style={styles.smallMap}
              />
            )}
            {dataToPrint.arrivalMap && (
              <Image
                src={arrivalMap?.getCanvas().toDataURL('image/png', 1)}
                style={styles.smallMap}
              />
            )}
          </View>
        )}
        {dataToPrint.instructions && (
          <InstructionsList bikeStationsMap={bikeStationsMap} route={route} />
        )}
        <Text
          fixed
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
          style={styles.pageNumber}
        />
      </Page>
    </Document>
  );
}

export default PrintedRouteLayout;
